import React, { useMemo, useState } from "react";

import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import { mockDrillStatusEnum } from "common/enums";

import InputFieldText from "Components/Common/InputFieldText";

const initialFilters = {
  selectedStatusId: "",
  fromDate: null,
  toDate: null,
  buildingName: null,
};

function FireSafetyMockDrillDetailFilter({
  setQuery,
  resetPage = () => {},
  t,
}) {
  const isExtraLargeScreen = useMediaQuery("(max-width: 1620px)");

  const [
    { selectedStatusId, fromDate, toDate, buildingName },
    setSelectedFilteredOption,
  ] = useState(initialFilters);

  const mockDrillStatusOption = useMemo(() => [
    { label: "All", value: "" },
    ...mockDrillStatusEnum,
  ]);

  const clearBtnHandler = () => {
    resetPage();
    setSelectedFilteredOption(initialFilters);
    setQuery({});
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    resetPage();
    setQuery(() => ({
      searchStatus: selectedStatusId,
      fromDate: fromDate?.isValid() ? fromDate.format("MM-DD-YYYY") : "",
      toDate: toDate?.isValid() ? toDate.format("MM-DD-YYYY") : "",
      buildingName,
    }));
  };

  return (
    <>
      <form onSubmit={(e) => searchBtnHandler(e)}>
        <Grid container columnSpacing={2} className="mt-2">
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              options={mockDrillStatusOption}
              value={
                mockDrillStatusOption.find(
                  (status) => status.value === selectedStatusId
                ) || ""
              }
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t("Select Status")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStatusId: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("Start Date")}
              name="fromDate"
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={fromDate ?? null}
              disableFuture
              maxDate={toDate}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  fromDate: date,
                }))
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("End Date")}
              name="toDate"
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={toDate ?? null}
              disableFuture
              minDate={fromDate}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  toDate: date,
                }))
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <InputFieldText
              label={t("Building Name")}
              className="w-100"
              value={buildingName ?? ""}
              placeholder={t("Enter Building Name")}
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  buildingName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              type="submit"
              className="me-3 mb-2"
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="me-3 mb-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

FireSafetyMockDrillDetailFilter.propTypes = {
  setQuery: PropTypes.func,
  resetPage: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyMockDrillDetailFilter);
